import { useState, useEffect, useCallback } from "react";
import { isVideoFile } from "utils/fileType";

export const useMediaPreloader = (url: string) => {
	const [isMediaLoaded, setIsMediaLoaded] = useState(false);
	const [loadingError, setLoadingError] = useState<string | null>(null);

	const preloadVideo = useCallback(async (videoUrl: string) => {
		try {
			const existingVideo = document.querySelector(
				`video[data-preload="${videoUrl}"]`
			);
			if (!existingVideo) {
				const video = document.createElement("video");
				video.src = videoUrl;
				video.preload = "metadata";
				video.muted = true;
				video.style.display = "none";
				video.setAttribute("data-preload", videoUrl);
				document.body.appendChild(video);

				return () => {
					if (document.body.contains(video)) {
						document.body.removeChild(video);
					}
				};
			}
			return () => {};
		} catch (error) {
			setLoadingError(`Failed to preload video: ${error}`);
			return () => {};
		}
	}, []);

	const preloadImage = useCallback(async (imageUrl: string) => {
		return new Promise<() => void>((resolve, reject) => {
			const img = new Image();

			img.onload = () => {
				setIsMediaLoaded(true);
				resolve(() => {});
			};

			img.onerror = () => {
				setLoadingError(`Failed to load image: ${imageUrl}`);
				reject(new Error(`Image load failed: ${imageUrl}`));
			};

			img.src = imageUrl;
		});
	}, []);

	useEffect(() => {
		if (!url) {
			return;
		}

		let cleanup: (() => void) | null = null;
		let isCancelled = false;

		const loadMedia = async () => {
			try {
				setLoadingError(null);
				const isVideo = isVideoFile(url);

				if (isVideo) {
					cleanup = await preloadVideo(url);
					if (!isCancelled) {
						setTimeout(() => setIsMediaLoaded(true), 3000);
					}
				} else {
					cleanup = await preloadImage(url);
				}
			} catch (error) {
				if (!isCancelled) {
					console.warn("Media preload failed:", error);
					setTimeout(() => setIsMediaLoaded(true), 1000);
				}
			}
		};

		loadMedia();

		return () => {
			isCancelled = true;
			if (cleanup) {
				cleanup();
			}
		};
	}, [url, preloadVideo, preloadImage]);

	return {
		isMediaLoaded,
		loadingError,
		isLoading: !isMediaLoaded && !loadingError
	};
};
